import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { DmaMapping } from '../../types/DspEntity';
import { ITab } from './DspAdGroupDictsTab';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import ApiService from '../../ApiService';
import { AlertColor, Box, Button, InputAdornment, Modal, Switch, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import theme from '../../theme';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessGuard from '../../components/AccessGuard';
import { USER_GROUP_ADMIN } from '../../types/User';
import TabPanel from '../../components/TabPanel';
import Column from '../../components/Column';
import Row from '../../components/Row';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CDGrid, { CustomPagination } from '../../components/CDGrid';
import { GridColDef, GridFooterContainer, GridSortModel } from '@mui/x-data-grid';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';

function DmaGridFooter(props: { totalElements: number; numberOfElements: number }) {
    return (
        <GridFooterContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                <CustomPagination />
                <span>
                    Showing {props.numberOfElements} of {props.totalElements} records
                </span>
            </div>
        </GridFooterContainer>
    );
}

const DmaDataMappingTab = ({ tab, activeTab }: { tab: ITab; activeTab: number }) => {
    const defaultPageSize: number = 50;
    const pageValueArray = [10, 20, 50, 75, 100];
    const [list, setList] = useState<DmaMapping[] | null>(null);
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [dma, setDma] = useState('');
    const [county, setCounty] = useState('');
    const [dmaDescription, setDmaDescription] = useState('');
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [country, setCountry] = useState('');
    const [open, setOpen] = useState(false);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);
    const [search, setSearch] = useState('');
    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });

    const handleChange = (event: SelectChangeEvent<string>) => {
        const value = parseInt(event?.target?.value);
        setPage(0);
        if (pageValueArray.includes(value)) {
            setPageSize(value);
        } else {
            setPageSize(defaultPageSize);
        }
    };

    const resetForm = () => {
        setCity('');
        setState('');
        setCounty('');
        setZip('');
        setDma('');
        setCountry('');
        setDmaDescription('');
    };

    const handleClose = () => {
        resetForm();
        setOpen(false);
    };

    const handleOpen = () => {
        resetForm();
        setOpen(true);
    };

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    const editModalStyles = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        borderRadius: '8px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };

    const fetchData = async () => {
        const response: AxiosPageableResponse<DmaMapping> = await ApiService.getDmaMappings(
            page,
            pageSize,
            sortModel,
            search
        );
        if (response.status === 200) {
            setList(response.data.content);
            setTotalElements(response.data.totalElements);
            setNumberOfElements(response.data.numberOfElements);
        }
    };

    const handleSwitchChange = (id: number) => (event: ChangeEvent<HTMLInputElement>) => {
        if (!list) return;
        const checked = event.target.checked;

        setList(
            list.map((row) => {
                if (row.id === id) {
                    const newRow = { ...row, disabled: checked };
                    ApiService.updateDmaMapping(newRow);
                    return newRow;
                }
                return row;
            })
        );
    };

    useEffect(() => {
        fetchData();
    }, [page, pageSize, sortModel, search]);

    //function to send Data for Dma Creation
    function dmaMappingSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        ApiService.addDmaMapping({
            city: city,
            state: state,
            county: county,
            zip: zip,
            dma: dma,
            dmaDescription: dmaDescription,
            country: country,
        }).then((newItem) => {
            if (!list) return;
            setList([...list, newItem.data]);
            showInfoMessage('success', 'DMA has been successfully created.');
        });
        handleClose();
    }

    const columns: GridColDef[] = [
        {
            flex: 1,
            minWidth: 100,
            field: 'id',
            headerName: 'ID',
            display: 'flex',
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'dmaDescription',
            headerName: 'DMA Description',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        DMA Description
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'dma',
            headerName: 'DMA Code',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        DMA Code
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 2,
            minWidth: 100,
            field: 'city',
            headerName: 'City',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        City
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'state',
            headerName: 'State',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        State
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'county',
            headerName: 'County',
            editable: true,
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        County
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'zip',
            headerName: 'Zip',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Zip
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'country',
            headerName: 'Country',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Country
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            preProcessEditCellProps: (params) => {
                return {
                    ...params.props,
                    error: !params.props.value?.trim(),
                };
            },
        },
        {
            flex: 1,
            minWidth: 100,
            field: 'disabled',
            headerName: 'Disable',
            type: 'boolean',
            renderCell: (params) => {
                const value = params.value;
                return <Switch checked={value} onChange={handleSwitchChange(params.id as number)} />;
            },
        },
    ];

    return (
        <AccessGuard accessGroup={USER_GROUP_ADMIN}>
            <TabPanel index={tab.id} value={activeTab} sx={{ p: 0 }}>
                <InfoMessage {...infoMessage} />
                <Column gap={2}>
                    <Row justifyContent="space-between" alignItems="center">
                        <Row gap={0} sx={{ width: '100%' }}>
                            <Typography component="span" color="text.primary" variant="subtitle2" fontWeight={500}>
                                Instruction:{' '}
                                <Typography
                                    component="span"
                                    color="text.secondary"
                                    variant="subtitle2"
                                    fontWeight={500}
                                >
                                    {tab.description}
                                </Typography>
                            </Typography>
                        </Row>
                        <Row flex={0} justifyContent="flex-end">
                            <Button variant="text" onClick={handleOpen} sx={{ marginTop: '-1%' }}>
                                Add DMA
                            </Button>

                            <FormControl sx={{ minWidth: 120 }} size="small">
                                <InputLabel id="demo-select-small-label">Rows Per Page</InputLabel>
                                <Select
                                    labelId="demo-select-small-label"
                                    id="demo-select-small"
                                    value={pageSize.toString()}
                                    label="Rows Per Page"
                                    onChange={handleChange}
                                >
                                    {pageValueArray?.map((value) => {
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Row>
                    </Row>
                    <Row>
                        <TextField
                            size="small"
                            placeholder="Search by city, state, zip, DMA code, county..."
                            onChange={(e) => {
                                const value = e.target.value;
                                if (searchTimeout.current) clearTimeout(searchTimeout.current);
                                searchTimeout.current = setTimeout(() => {
                                    setPage(0);
                                    setSearch(value);
                                }, 400);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 400 }}
                        />
                    </Row>
                    <Modal
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                    >
                        <Box sx={editModalStyles}>
                            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                                Add DMA
                            </Typography>
                            <form onSubmit={dmaMappingSubmit} style={{ width: '100%' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 2,
                                        width: '100%',
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="DMA Description"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setDmaDescription(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="DMA Code"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setDma(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="City"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setCity(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="State"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setState(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="County"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setCounty(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Zip"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setZip(e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Country"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => setCountry(e.target.value)}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                                    <Button
                                        variant="contained"
                                        sx={{ width: '120px' }}
                                        startIcon={<SaveIcon />}
                                        type="submit"
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{ width: '120px' }}
                                        onClick={handleClose}
                                        startIcon={<CloseIcon />}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>
                    <CDGrid
                        id="dmaMapping"
                        variant="contained"
                        rowCount={totalElements}
                        paginationModel={{ page: page, pageSize: pageSize }}
                        columns={columns}
                        onPaginationModelChange={(model) => {
                            setPage(model.page);
                            setPageSize(model.pageSize);
                        }}
                        onSortModelChange={setSortModel}
                        paginationMode={'server'}
                        sortingMode={'server'}
                        filterMode={'server'}
                        disableColumnFilter
                        rows={list === null ? [] : list}
                        processRowUpdate={(newRow) => {
                            ApiService.updateDmaMapping(newRow);
                            setList((prevRows) =>
                                prevRows === null
                                    ? []
                                    : prevRows.map((row) => {
                                          if (row.id === newRow.id) {
                                              return newRow;
                                          } else {
                                              return row;
                                          }
                                      })
                            );
                            return newRow;
                        }}
                        onProcessRowUpdateError={() => {}}
                        slots={{
                            footer: () => (
                                <DmaGridFooter totalElements={totalElements} numberOfElements={numberOfElements} />
                            ),
                        }}
                    />
                </Column>
            </TabPanel>
        </AccessGuard>
    );
};

export default DmaDataMappingTab;
